import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, CircleHelp, Video } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import FeedbackModal from "../Feedback/FeedbackModal";

const guides = [
  { title: "Quick Start", slug: "quick-start" },
  { title: "Submit Threat Model", slug: "submit-threat-model" },
  { title: "Interact with Results", slug: "interact-with-threat-model-results" },
  { title: "Replay Threat Model", slug: "replay-threat-model" },
  { title: "Using Attack Trees", slug: "using-attack-trees" },
  { title: "Using Sentry", slug: "using-sentry" },
  { title: "Using Spaces", slug: "using-spaces" },
  { title: "Versioning Threat Models", slug: "versioning-threat-models" },
  { title: "Collaborate on Threat Models", slug: "collaborate-on-threat-models" },
];

const videos = [
  {
    title: "Video - Create Threat Model",
    url: "https://docs.philips.com/:v:/g/personal/santosh_pai_philips_com/IQBSb18Hm7NSRoxOMzUYxyvpAdJ9eSuP4yhKhnt249Kd7ow?nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJPbmVEcml2ZUZvckJ1c2luZXNzIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXciLCJyZWZlcnJhbFZpZXciOiJNeUZpbGVzTGlua0NvcHkifX0&e=RI4QHL",
  },
  {
    title: "Video - Versioning Threat Model",
    url: "https://docs.philips.com/:v:/g/personal/santosh_pai_philips_com/IQCke44WatM-TrvXGT7EFBDgAZ8_PckLKCS6oV8j86v66WQ?nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJPbmVEcml2ZUZvckJ1c2luZXNzIiwicmVmZXJyYWxBcHBQbGF0Zm9ybSI6IldlYiIsInJlZmVycmFsTW9kZSI6InZpZXciLCJyZWZlcnJhbFZpZXciOiJNeUZpbGVzTGlua0NvcHkifX0&e=YPvtc3",
  },
];

export function NavGuides() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, state } = useSidebar();
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  const isGuideActive = location.pathname.startsWith("/guides");

  const handleGuideClick = (slug) => {
    navigate(`/guides/${slug}`);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              tooltip="Guides"
              isActive={isGuideActive}
              className="cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <BookOpen className="size-5 shrink-0" />
              {state !== "collapsed" && <span className="ml-2">Documentation</span>}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={state === "collapsed" ? 14 : 14}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Videos
              </DropdownMenuLabel>
              {videos.map((video) => (
                <DropdownMenuItem
                  key={video.url}
                  onClick={() => window.open(video.url, "_blank", "noopener,noreferrer")}
                  className="cursor-pointer"
                >
                  <Video className="mr-2 size-3.5 shrink-0" />
                  <span>{video.title}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>          
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Quick Guides
              </DropdownMenuLabel>
              {guides.map((guide) => {
                const isActive = location.pathname === `/guides/${guide.slug}`;
                return (
                  <DropdownMenuItem
                    key={guide.slug}
                    onClick={() => handleGuideClick(guide.slug)}
                    className={`cursor-pointer ${isActive ? "bg-accent" : ""}`}
                  >
                    <span>{guide.title}</span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuGroup>

          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip="Feedback"
          onClick={() => setFeedbackVisible(true)}
          className="cursor-pointer"
        >
          <CircleHelp className="size-5 shrink-0" />
          {state !== "collapsed" && <span className="ml-2">Feedback</span>}
        </SidebarMenuButton>
      </SidebarMenuItem>
      <FeedbackModal visible={feedbackVisible} onDismiss={() => setFeedbackVisible(false)} />
    </SidebarMenu>
  );
}

export default NavGuides;
